import React, { memo, useCallback, useMemo, useState } from "react";
import { GroundRule, CheckInIcon, CheckOutIcon, WifiIcon, DescriptionIcon, EssentialIcon, PetsIcon, CheckMarkPurple, PlusIcon } from "../../../../../../AssetComponents/Images";
import CancellationIcon from "../../../../../../Components/Rent/Assests/CancellationIcon";
import { PurpleButton } from "../../../../../../Components/Buttons/PurpleButton";
import { NumberSpin } from "../../../../../../Components/Spin/NumberSpin";
import { Toggle } from "../../../../../../Components/Toggle/Toggle";
import TimePicker from "react-time-picker";
import Modal from "react-responsive-modal";
import AmenitiesIcon from "../../../../../../assets/images/Dashboard/Dashboard/Amenities";
import { AmenityIcons } from "../../../../../../Components/Rent/AmenityIcons";
import { getAmenityLabel, countSelectedAmenities } from "../../utils/amenityUtils";
import { AMENITY_GROUPS, AMENITY_CATEGORIES } from "../../constants/amenitiesConfig";
import "react-time-picker/dist/TimePicker.css";

const SelectedAmenities = memo(({ amenities }) => {
  const entries = useMemo(
    () =>
      Object.entries(amenities || {}).filter(
        ([, list]) => Array.isArray(list) && list.length > 0
      ),
    [amenities]
  );

  if (entries.length === 0) {
    return (
      <div className="px-[14px] text-sm text-[#959595]">
        No amenities added yet. Click "View all" to add amenities.
      </div>
    );
  }

  return (
    <div className="px-[14px] space-y-4 max-h-[220px] overflow-auto pr-1">
      {entries.map(([category, items]) => (
        <div key={category} className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-[#9B9B9B]">
            {category}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map((item) => (
              <div key={item} className="flex items-center text-sm text-[#303030]">
                <AmenityIcons item={item} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
});

SelectedAmenities.displayName = "SelectedAmenities";

const PropertyDetailsForm = memo(({
  metaDetails,
  setMetaDetails,
  addressInput,
  setAddressInput,
  addressDetails,
  checkInModal,
  setCheckInModal,
  checkOutModal,
  setCheckOutModal,
}) => {
  const [amenitiesModal, setAmenitiesModal] = useState(false);
  const [amenitySearch, setAmenitySearch] = useState("");
  const [currentCategory, setCurrentCategory] = useState("All");

  const selectedAmenities = metaDetails.amenities || {};
  const totalSelected = countSelectedAmenities(selectedAmenities);

  const normalizedGroups = useMemo(() => {
    const trimmed = amenitySearch.trim().toLowerCase();
    if (!trimmed) return AMENITY_GROUPS;

    return AMENITY_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        getAmenityLabel(item).toLowerCase().includes(trimmed)
      ),
    })).filter((group) => group.items.length > 0);
  }, [amenitySearch]);

  const shouldRenderGroup = useCallback(
    (groupKey) => currentCategory === "All" || amenitySearch || groupKey === currentCategory,
    [currentCategory, amenitySearch]
  );

  const toggleAmenity = useCallback(
    (categoryKey, amenityKey) => {
      setMetaDetails((prev) => {
        const previousAmenities = prev.amenities || {};
        const currentList = previousAmenities[categoryKey] || [];
        const exists = currentList.includes(amenityKey);

        const updatedList = exists
          ? currentList.filter((item) => item !== amenityKey)
          : [...currentList, amenityKey];

        const nextAmenities = { ...previousAmenities };

        if (updatedList.length > 0) {
          nextAmenities[categoryKey] = updatedList;
        } else {
          delete nextAmenities[categoryKey];
        }

        return {
          ...prev,
          amenities: nextAmenities,
        };
      });
    },
    [setMetaDetails]
  );

  const isAmenitySelected = useCallback(
    (categoryKey, amenityKey) =>
      Array.isArray(selectedAmenities[categoryKey]) &&
      selectedAmenities[categoryKey].includes(amenityKey),
    [selectedAmenities]
  );

  const clearAllAmenities = useCallback(() => {
    setMetaDetails((prev) => ({
      ...prev,
      amenities: {},
    }));
  }, [setMetaDetails]);

  return (
    <div className="space-y-[20px] w-full px-[16px] py-[24px] bg-white rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
      {/* Building Name */}
              <div className="px-[14px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-[10px]">
            <div>Building Name</div>
          </div>
          <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
            <input
              placeholder="Building name"
              className="outline-none w-full font-normal text-[14px]"
              value={metaDetails.buildingName || ""}
              onChange={(e) =>
                setMetaDetails({
                  ...metaDetails,
                  buildingName: e.target.value,
                })
              }
            />
          </div>
        </div>

      {/* Ground Rules */}
      <div className="px-[14px] flex flex-col sm:flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <GroundRule />
          <div>Ground Rules</div>
        </div>
        <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
          <textarea
            placeholder="Ground rules"
            className="h-[80px] outline-none w-full font-normal text-[14px] resize-none"
            value={metaDetails.groundRule || ""}
            onChange={(e) =>
              setMetaDetails({
                ...metaDetails,
                groundRule: e.target.value,
              })
            }
          />
        </div>
      </div>

      {/* Check In Hours */}
      <div className="px-[14px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <CheckInIcon className="w-[16px]" />
          <div>Check in Hours</div>
        </div>
        <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
          <input
            placeholder="00:00"
            className="outline-none w-full font-normal text-[14px]"
            value={metaDetails.checkIn || ""}
            onClick={() => setCheckInModal(true)}
            readOnly
          />
          <Modal
            open={checkInModal}
            center
            onClose={() => setCheckInModal(false)}
            classNames={{
              modal:
                "min-w-[300px] min-h-[200px] rounded-[8px] border-[1px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]",
            }}
          >
            <div className="text-[18px] font-semibold">Check In</div>
            <div className="mt-[30px]">
              <TimePicker
                clockIcon={null}
                clearIcon={null}
                value={metaDetails.checkIn}
                disableClock
                onChange={(value) =>
                  setMetaDetails({ ...metaDetails, checkIn: value })
                }
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between mt-6">
              <div
                className="text-[#5b1dee] underline cursor-pointer"
                onClick={() =>
                  setMetaDetails({ ...metaDetails, checkIn: null })
                }
              >
                Clear times
              </div>
              <PurpleButton
                text="Save"
                onClick={() => setCheckInModal(false)}
              />
            </div>
          </Modal>
        </div>
      </div>

      {/* Check Out Hours */}
      <div className="px-[14px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <CheckOutIcon className="w-[16px]" />
          <div>Check out Hours</div>
        </div>
        <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
          <input
            placeholder="00:00"
            className="outline-none w-full font-normal text-[14px]"
            value={metaDetails.checkOut || ""}
            onClick={() => setCheckOutModal(true)}
            readOnly
          />
          <Modal
            open={checkOutModal}
            center
            onClose={() => setCheckOutModal(false)}
            classNames={{
              modal:
                "min-w-[300px] min-h-[200px] rounded-[8px] border-[1px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]",
            }}
          >
            <div className="text-[18px] font-semibold">Check Out</div>
            <div className="mt-[30px]">
              <TimePicker
                clockIcon={null}
                clearIcon={null}
                value={metaDetails.checkOut}
                disableClock
                onChange={(value) =>
                  setMetaDetails({ ...metaDetails, checkOut: value })
                }
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between mt-6">
              <div
                className="text-[#5b1dee] underline cursor-pointer"
                onClick={() =>
                  setMetaDetails({ ...metaDetails, checkOut: null })
                }
              >
                Clear times
              </div>
              <PurpleButton
                text="Save"
                onClick={() => setCheckOutModal(false)}
              />
            </div>
          </Modal>
        </div>
      </div>

      {/* WiFi Password */}
      <div className="px-[14px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <WifiIcon className="w-[16px]" />
          <div>WiFi Password</div>
        </div>
        <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
          <input
            placeholder="Password"
            className="outline-none w-full font-normal text-[14px]"
            value={metaDetails.wifiPass || ""}
            onChange={(e) =>
              setMetaDetails({ ...metaDetails, wifiPass: e.target.value })
            }
          />
        </div>
      </div>

      {/* Description */}
      <div className="px-[14px] flex flex-col sm:flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <DescriptionIcon />
          <div>Description</div>
        </div>
        <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
          <textarea
            placeholder="Description"
            className="h-[80px] outline-none w-full font-normal text-[14px] resize-none"
            value={metaDetails.description || ""}
            onChange={(e) =>
              setMetaDetails({
                ...metaDetails,
                description: e.target.value,
              })
            }
          />
        </div>
      </div>

      {/* Cancellation Policy */}
      <div className="px-[14px] flex flex-col sm:flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <CancellationIcon />
          <div>Cancellation policy</div>
        </div>
        <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
          <textarea
            placeholder="Cancellation policy"
            className="h-[80px] outline-none w-full font-normal text-[14px] resize-none"
            value={metaDetails.cancellation || ""}
            onChange={(e) =>
              setMetaDetails({
                ...metaDetails,
                cancellation: e.target.value,
              })
            }
          />
        </div>
      </div>

      {/* Address */}
      <div className="px-[14px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-[10px]">
          <div>Address</div>
        </div>
        <div className="bg-white px-[12px] w-full sm:w-[260px] py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
          <input
            placeholder="Address"
            className="outline-none w-full font-normal text-[14px]"
            value={addressInput}
            onChange={(e) => setAddressInput(e.target.value)}
          />
        </div>
      </div>

      {/* City and State */}
      <div className="px-[14px] flex flex-col sm:flex-row gap-4 sm:gap-[40px]">
        <div className="w-full space-y-[10px]">
          <div className="text-[14px] font-normal">City</div>
          <div className="bg-white px-[12px] w-full py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
            <input
              value={addressDetails?.city || ""}
              disabled
              className="bg-white outline-none w-full font-normal text-[14px]"
            />
          </div>
        </div>
        <div className="w-full space-y-[10px]">
          <div className="text-[14px] font-normal">State</div>
          <div className="bg-white px-[12px] w-full py-[14px] border-[1px] border-[#E3E3E3] rounded-[12px]">
            <input
              value={addressDetails?.state || ""}
              disabled
              className="bg-white outline-none w-full font-normal text-[14px]"
            />
          </div>
        </div>
      </div>

      {/* Amenities Summary */}
      <div className="px-[14px] flex items-center justify-between">
        <div className="flex items-center gap-[4px]">
          <AmenitiesIcon />
          <div>Amenities</div>
          {totalSelected > 0 && (
            <span className="text-sm text-[#959595]">({totalSelected})</span>
          )}
        </div>
        <div
          onClick={() => setAmenitiesModal(true)}
          className="text-[#5b1dee] text-[14px] cursor-pointer hover:underline"
        >
          View all
        </div>
      </div>
      <SelectedAmenities amenities={metaDetails.amenities} />

      {/* Amenities Modal */}
      <Modal
        open={amenitiesModal}
        center
        onClose={() => {
          setAmenitiesModal(false);
          setAmenitySearch("");
          setCurrentCategory("All");
        }}
        classNames={{
          modal:
            "w-[90vw] max-w-[520px] rounded-[12px] border-[1px] border-[#E3E3E3] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] p-0",
        }}
      >
        <div className="p-[20px] space-y-[16px]">
          <div className="text-[18px] font-semibold">Add amenities</div>

          {/* Search Input */}
          <div className="bg-white px-[12px] py-[10px] border-[1px] border-[#E3E3E3] rounded-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
            <input
              value={amenitySearch}
              onChange={(e) => {
                setAmenitySearch(e.target.value);
                if (e.target.value) setCurrentCategory("All");
              }}
              placeholder="Search amenities"
              className="outline-none w-full font-normal text-[14px]"
            />
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-[8px]">
            {AMENITY_CATEGORIES.map((item) => (
              <div
                key={item}
                onClick={() => {
                  setCurrentCategory(item);
                  setAmenitySearch("");
                }}
                className={`text-[13px] cursor-pointer select-none px-[12px] py-[6px] rounded-[10px] border-[1px] border-[#E3E3E3] transition-colors ${
                  currentCategory === item && !amenitySearch
                    ? "bg-[#dddddd]"
                    : "hover:bg-[#f0f0f0]"
                }`}
              >
                {item}
              </div>
            ))}
          </div>

          {/* Amenities Grid */}
          <div className="grid grid-cols-2 gap-[10px] max-h-[280px] overflow-auto scrollbarwidth p-[8px] bg-[#fdfdfd] rounded-[10px]">
            {normalizedGroups.length === 0 ? (
              <div className="col-span-2 text-center text-[#999999] py-6">
                No amenities match "{amenitySearch}".
              </div>
            ) : (
              normalizedGroups.map((group) => {
                if (!shouldRenderGroup(group.key)) return null;

                return group.items.map((amenity) => {
                  const selected = isAmenitySelected(group.key, amenity);
                  return (
                    <div
                      key={`${group.key}-${amenity}`}
                      onClick={() => toggleAmenity(group.key, amenity)}
                      className="w-full flex items-center justify-between px-[10px] py-[8px] bg-white rounded-[12px] border border-[#E3E3E3] cursor-pointer hover:bg-[#fafafa] transition-colors"
                    >
                      <div className="flex items-center">
                        <AmenityIcons item={amenity} />
                      </div>
                      {selected ? (
                        <CheckMarkPurple className="flex-shrink-0" />
                      ) : (
                        <PlusIcon className="flex-shrink-0" />
                      )}
                    </div>
                  );
                });
              })
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-[8px]">
            <div
              onClick={clearAllAmenities}
              className="text-[#5b1dee] text-[14px] cursor-pointer hover:underline"
            >
              Clear amenities
            </div>
            <PurpleButton
              text="Save"
              onClick={() => {
                setAmenitiesModal(false);
                setAmenitySearch("");
                setCurrentCategory("All");
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Essentials Section */}
      <div className="px-[14px] flex items-start justify-between">
        <div className="flex items-center gap-[4px]">
          <EssentialIcon />
          <div>Essentials</div>
        </div>
      </div>
      <div className="space-y-[10px]">
        <div className="px-[14px] flex items-center justify-between">
          <div className="text-[14px] font-normal">Guests</div>
          <NumberSpin
            value={metaDetails.essentials.guests}
            min={1}
            onChange={(value) =>
              setMetaDetails({
                ...metaDetails,
                essentials: { ...metaDetails.essentials, guests: value },
              })
            }
          />
        </div>
        <div className="px-[14px] flex items-center justify-between">
          <div className="text-[14px] font-normal">Bedrooms</div>
          <NumberSpin
            value={metaDetails.essentials.bedrooms}
            onChange={(value) =>
              setMetaDetails({
                ...metaDetails,
                essentials: { ...metaDetails.essentials, bedrooms: value },
              })
            }
          />
        </div>
        <div className="px-[14px] flex items-center justify-between">
          <div className="text-[14px] font-normal">Beds</div>
          <NumberSpin
            value={metaDetails.essentials.beds}
            onChange={(value) =>
              setMetaDetails({
                ...metaDetails,
                essentials: { ...metaDetails.essentials, beds: value },
              })
            }
          />
        </div>
        <div className="px-[14px] flex items-center justify-between">
          <div className="text-[14px] font-normal">Bathrooms</div>
          <NumberSpin
            value={metaDetails.essentials.bathrooms}
            onChange={(value) =>
              setMetaDetails({
                ...metaDetails,
                essentials: { ...metaDetails.essentials, bathrooms: value },
              })
            }
          />
        </div>
        <div className="px-[14px] flex items-center justify-between">
          <div className="text-[14px] font-normal">Meters</div>
          <div className="flex items-center border-[2px] rounded-md w-[98px] px-1">
            <input
              value={metaDetails.essentials.square}
              onChange={(e) =>
                setMetaDetails({
                  ...metaDetails,
                  essentials: {
                    ...metaDetails.essentials,
                    square: e.target.value,
                  },
                })
              }
              className="outline-none w-full text-center pl-2"
              min={1}
            />
            <div className="text-[#959595] text-[14px] items-top flex">
              <span>M</span>
              <span className="text-[10px] top-0">2</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pets Section */}
      <div className="px-[14px] flex items-start justify-between">
        <div className="flex items-center gap-[4px]">
          <PetsIcon />
          <div>Pets</div>
        </div>
      </div>
      <div className="px-[14px] flex items-center justify-between">
        <div className="text-[14px] font-normal">Allowing pets</div>
        <Toggle
          status={metaDetails.pets}
          onChange={() =>
            setMetaDetails({ ...metaDetails, pets: !metaDetails.pets })
          }
        />
      </div>
    </div>
  );
});

PropertyDetailsForm.displayName = "PropertyDetailsForm";

export default PropertyDetailsForm;
